import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import { EmptyTeamState } from '@/frontend/features/Home/EmptyTeamState'
import { useAgentChatController } from '@/frontend/pages/Dashboard/AgentChats/useAgentChatController'
import { latestProcedureForConversation } from '@/frontend/features/AgentChats/tools/procedureActivity'
import { persistSelectedWorker, selectedWorkerStorageContextFromProps } from '@/frontend/layouts/worker-first/useSelectedWorker'
import { CreateIntro } from './chatCreate/CreateIntro'
import type { ChatCreateWorker } from './chatCreate/types'
import type { AgentChatDigitalWorker } from '@/frontend/pages/Dashboard/AgentChats/types'
import type { SharedProps } from '@/frontend/types/inertia'
import { BusinessProcessConversationWorkspace } from './BusinessProcessConversationWorkspace'

// O nome técnico do plugin permanece sz-procedures; na UI, o termo é Processo.
const INTENT_PREFIX = 'Quero criar um novo processo: '
interface Props {
  page_title: string
  organization_id: string
  workers: ChatCreateWorker[]
}

/**
 * Criação de processos por conversa (SZ-1706): substitui o formulário guiado.
 * Fase 1 é a anatomia de agent_chats/new (saudação + seletor + composer); o
 * primeiro envio cria uma conversa NORMAL de Agent Chat (fonte
 * business_process_create) e a thread embute nesta página, sem coluna de
 * sessões e sem publicar na sidebar global. Quando o intake conversacional
 * cria o Processo, a sessão recebe `created_business_process` (broadcast +
 * detail) e a página navega para o detalhe worker-scoped. Cada visita começa
 * do zero; a conversa segue acessível na página de chat como qualquer outra.
 */
function BusinessProcessesNew({ page_title, organization_id, workers }: Props) {
  const { props: shared } = usePage<SharedProps>()
  const userName = shared.auth?.user?.name ?? null
  const procedureChatUxEnabled = shared.featureFlags?.procedure_chat_ux_enabled ?? false
  const lastWorkerId = shared.lastDigitalWorkerId ?? null
  const selectionStorageContext = useMemo(
    () => selectedWorkerStorageContextFromProps(shared),
    [shared.auth.user?.id, shared.auth.user?.active_organization?.id],
  )

  const readyWorkers = useMemo(() => workers.filter((worker) => worker.chat_ready), [workers])
  const defaultTarget = readyWorkers.find((w) => w.id === lastWorkerId) || readyWorkers[0] || null
  const [selectedId, setSelectedId] = useState<string | null>(defaultTarget?.id ?? null)
  const target = readyWorkers.find((w) => w.id === selectedId) || defaultTarget

  const [prompt, setPrompt] = useState('')
  const navigatedRef = useRef(false)

  // O servidor resolve o agente padrão quando cria a sessão.
  const chatWorker = useMemo<AgentChatDigitalWorker | null>(() => (
    target
      ? {
        id: target.id,
        name: target.name,
        job_title: target.job_title,
        avatar_url: target.avatar_url,
        can_send: target.chat_ready,
        agent_id: null,
        agent_name: null,
      }
      : null
  ), [target])

  const chat = useAgentChatController({
    organizationId: organization_id,
    digitalWorker: chatWorker,
    sessionCreateSource: 'business_process_create',
  })

  // Navegação pós-criação: dispara uma única vez, tanto pelo broadcast
  // (session_upsert) quanto pelo detail recarregado após reconexão. O destino
  // é a página de ciclo de vida do processo (rascunho, aprovação, documentos),
  // não a de operação worker-scoped.
  const createdProcess = chat.session?.created_business_process ?? null
  const procedurePreview = useMemo(() => (
    procedureChatUxEnabled
      ? latestProcedureForConversation(
        chat.session?.messages || [],
        chat.activity,
        chat.session?.procedure_presentation || null,
      )
      : null
  ), [chat.activity, chat.session?.messages, chat.session?.procedure_presentation, procedureChatUxEnabled])
  useEffect(() => {
    if (!createdProcess?.id || navigatedRef.current) return

    navigatedRef.current = true
    router.visit(`/dashboard/business_processes/${createdProcess.id}`)
  }, [createdProcess])

  const submitFirstMessage = async () => {
    const text = prompt
      .trim()
      .replace(/^(?:quero\s+)?criar\s+um\s+novo\s+(?:processo|procedimento)\s*:\s*/i, '')
      .trim()
    if (!target || !target.intake_enabled || !text) return
    if (chat.submissionPhase !== 'idle') return

    const original = prompt
    persistSelectedWorker(target.id, selectionStorageContext)
    setPrompt('')
    await chat.submitMessage(`${INTENT_PREFIX}${text}`, [], () => setPrompt(original))
  }

  const hasReady = readyWorkers.length > 0
  const conversationStarted = chat.session !== null

  return (
    <>
      <Head title={page_title} />
      {workers.length === 0 ? (
        <div className="mx-auto flex min-h-[70vh] w-full max-w-[620px] flex-col justify-center py-10">
          <EmptyTeamState />
        </div>
      ) : !hasReady || !target || !chatWorker ? (
        <div className="mx-auto flex min-h-[70vh] w-full max-w-[620px] flex-col justify-center py-10">
          <div
            data-testid="novo-processo-nenhum-pronto"
            className="rounded-xl border border-dashed border-border px-6 py-10 text-center"
          >
            <p className="text-sm font-medium text-foreground">
              Nenhum colaborador está pronto para conversar ainda
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Assim que a instância de um colaborador ficar pronta, a criação por conversa abre por aqui.
            </p>
          </div>
        </div>
      ) : !conversationStarted ? (
        <CreateIntro
          workers={workers}
          target={target}
          prompt={prompt}
          sending={chat.submissionPhase !== 'idle'}
          error={chat.visibleError}
          onSelect={setSelectedId}
          onPromptChange={setPrompt}
          onSubmit={submitFirstMessage}
        />
      ) : (
        <BusinessProcessConversationWorkspace
          chat={chat}
          worker={chatWorker}
          userName={userName}
          procedure={procedurePreview}
          showProcedureCanvas={procedureChatUxEnabled}
        />
      )}
    </>
  )
}

BusinessProcessesNew.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>

export default BusinessProcessesNew
